import { Fragment, useState, useEffect, useCallback, useRef } from "react";
import queryString from "query-string";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import makeStyles from "styles/muiStylesCompat/makeStyles";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";

import ViewContainer from "components/ViewContainer/ViewContainer";
import ButtonPrimary from "components/Buttons/ButtonPrimary";
import { parseRegistrationApiErrors } from "~/utils/registrationErrors";

import { createCompany, httpAcceptInviteToken } from "services/http/companies";

import AuthService from "services/http/AuthService";
const auth = new AuthService();

const styles = (theme) => ({
  fieldGrid: {
    marginBottom: 12,
  },
  toggleButton: {
    //backgroundColor: "#000",
  },
  toggleButtonSelected: {
    color: "#fff" + " !important",
    backgroundColor: theme.palette.primary.main + " !important",
  },
});

const useStyles = makeStyles(styles);

const RegisterAdmin = (props) => {
  const classes = useStyles();

  const [values, setValues] = useState({
    username: "",
    password: "",
  });
  const [saving, setSaving] = useState(false);
  const [invitationToken, setInvitationToken] = useState(null);

  const [errors, setErrors] = useState(null);
  const [terms, setTerms] = useState(false);
  const [sign, setSign] = useState(false);

  const [registerState, setRegisterState] = useState(0);
  const errorAlertRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = props;
  const { search } = location;
  const { t } = useTranslation();

  const handleAcceptInviteToken = useCallback(
    (companyId, token) => {
      setSaving(true);
      httpAcceptInviteToken(companyId, token).then(
        (response) => {
          setValues((prev) => ({ ...prev, ...response }));
          setSaving(false);
        },
        (_error) => {
          setSaving(false);
          enqueueSnackbar(
            t(
              "Kutsun tietojen lataus epäonnistui. Tarkista linkki tai ota yhteyttä tukeen.",
            ),
            { variant: "error" },
          );
        },
      );
    },
    [enqueueSnackbar, t],
  );

  useEffect(() => {
    if (search) {
      const parsed = queryString.parse(search);

      if (parsed.token && parsed.token.length < 64) {
        enqueueSnackbar(
          t(
            "Kutsulinkki on virheellinen. Tarkista, että sähköpostissa saatu linkki on täydellinen.",
          ),
          { variant: "error" },
        );
        navigate("/");
      }

      if (parsed.token) {
        setInvitationToken(parsed.token);
        if (parsed.company) {
          handleAcceptInviteToken(parsed.company, parsed.token);
        }
      }
    }
  }, [search, handleAcceptInviteToken, enqueueSnackbar, navigate, t]);

  const handleTerms = (event, newTerms) => {
    if (newTerms == null) return;
    setTerms(newTerms);
    if (newTerms === true) {
      setErrors((prev) => {
        if (!prev?.terms) return prev;
        const { terms: _terms, ...rest } = prev;
        return Object.keys(rest).length ? rest : null;
      });
    }
  };

  const handleSign = (event, newSign) => {
    if (newSign == null) return;
    setSign(newSign);
    if (newSign === true) {
      setErrors((prev) => {
        if (!prev?.sign) return prev;
        const { sign: _sign, ...rest } = prev;
        return Object.keys(rest).length ? rest : null;
      });
    }
  };

  const handleInputChange = (event) => {
    let { value, name } = event.target;

    if (invitationToken && ["company_name", "business_id", "email"].includes(name)) {
      return;
    }

    if (["email", "username"].indexOf(name) > -1) {
      value = value.toLowerCase();
    }

    setErrors((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      delete next[name];
      if (Object.keys(next).length === 0) return null;
      return next;
    });

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onSubmit = (event) => {
    event.preventDefault();

    const consentErrors = {};
    if (sign !== true) {
      consentErrors.sign = t(
        "Vahvista, että olet yrityksen nimenkirjoitusoikeudellinen",
      );
    }
    if (terms !== true) {
      consentErrors.terms = t("Hyväksy ehdot jatkaaksesi");
    }
    if (Object.keys(consentErrors).length > 0) {
      setErrors({ ...consentErrors, inValid: true });
      return;
    }

    setSaving(true);
    setErrors(null);

    validateForm().then(
      () => {
        handleRegister();
      },
      () => {
        setSaving(false);
      },
    );
  };

  const handleRegister = () => {
    setSaving(true);
    const request = {
      data: {
        ...values,
        email: values.email.toLowerCase(),
        username: values.email.toLowerCase(),
        accept_terms: true,
        first_name: values.first_name,
        last_name: values.last_name,
        password: values.password1,
        status: 100,
        ...(invitationToken
          ? {}
          : {
              name: values.company_name,
              business_id: values.business_id,
            }),
      },
      params: {
        token: invitationToken,
      },
    };

    if (invitationToken) {
      delete request.data.company_name;
      delete request.data.business_id;
      delete request.data.company;
    }

    const completeRegistration = () => {
      setRegisterState(1);
      setSaving(false);
    };

    const handleLoginAfterRegister = () => {
      auth.login(values.email.toLowerCase(), values.password1).then(
        () => {
          completeRegistration();
        },
        (_loginError) => {
          completeRegistration();
          enqueueSnackbar(
            t(
              "Tili luotiin. Jos et pääse automaattisesti sisään, kirjaudu manuaalisesti.",
            ),
            { variant: "warning" },
          );
        },
      );
    };

    if (invitationToken) // Register only user
    {
      auth.register(request).then(
        () => {
          handleLoginAfterRegister();
        },
        (errors) => {
          handleErrors(errors);
          setSaving(false);
        },
      );
    } else // Create company and register user
    {
      createCompany(request).then(
        () => {
          handleLoginAfterRegister();
        },
        (errors) => {
          handleErrors(errors);
          setSaving(false);
        },
      );
    }
  };

  const handleErrors = (apiError) => {
    const { fieldErrors, message } = parseRegistrationApiErrors(apiError, t);
    setErrors({
      ...fieldErrors,
      message,
      inValid: true,
    });
  };

  const goBack = () => {
    setRegisterState(0);
  };

  const goDashboard = () => {
    navigate("/dashboard/start");
  };

  const validationSchema = yup.object().shape({
    first_name: yup.string().required(t("Pakollinen kenttä")),
    last_name: yup.string().required(t("Pakollinen kenttä")),
    company_name: invitationToken
      ? yup.string().nullable()
      : yup.string().required(t("Pakollinen kenttä")),
    business_id: invitationToken
      ? yup.string().nullable()
      : yup.string().required(t("Pakollinen kenttä")),
    email: yup
      .string()
      .email(t("Sähköpostiosoite ei ole kelvollinen"))
      .required(t("Pakollinen kenttä")),
    password2: yup
      .string()
      .required(t("Pakollinen kenttä"))
      .min(8, t("Salasanan on oltava vähintään 8 merkkiä"))
      .oneOf(
        [yup.ref("password1"), null],
        t("Salasanan on täsmättävä uusi salasana kentän kanssa"),
      ),
    password1: yup
      .string()
      .required(t("Pakollinen kenttä"))
      .min(8, t("Salasanan on oltava vähintään 8 merkkiä")),
  });

  const validateForm = () => {
    return new Promise((resolve, reject) => {
      validationSchema
        .validate(values, { abortEarly: false })
        .then(() => {
          resolve(true);
        })
        .catch((err) => {
          const fieldErrors = { inValid: true };
          if (err?.inner?.length) {
            err.inner.forEach((validationError) => {
              if (validationError.path) {
                fieldErrors[validationError.path] = t(validationError.message);
              }
            });
          } else if (err?.path) {
            fieldErrors[err.path] = t(err.message);
          }
          setErrors(fieldErrors);
          reject("NO_GOOD");
        });
    });
  };

  const consentMessages = [errors?.sign, errors?.terms].filter(Boolean);
  const errorSummary =
    errors?.message ||
    (consentMessages.length ? consentMessages.join(" ") : null) ||
    (errors?.inValid
      ? t("Tarkista korostetut kentät ja yritä uudelleen.")
      : null);

  useEffect(() => {
    if (errorSummary) {
      errorAlertRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [errorSummary]);

  return (
    <Fragment>
      {registerState == 0 ? (
        <ViewContainer title={t("Rekisteröidy")}>
          <form className={classes.root} onSubmit={onSubmit}>
            <Grid container className={classes.container}>
              <Grid className={classes.fieldGrid} size={{ xs: 12 }}>
                <Typography variant="subtitle1">{t("Etunimi")}</Typography>
                <TextField
                  fullWidth
                  id="register-first_name"
                  name="first_name"
                  value={values.first_name}
                  onChange={handleInputChange}
                  margin="dense"
                  variant="outlined"
                  required
                  error={Boolean(errors?.first_name)}
                  helperText={errors?.first_name}
                />
              </Grid>

              <Grid className={classes.fieldGrid} size={{ xs: 12 }}>
                <Typography variant="subtitle1">{t("Sukunimi")}</Typography>
                <TextField
                  fullWidth
                  id="register-last_name"
                  name="last_name"
                  value={values.last_name}
                  onChange={handleInputChange}
                  margin="dense"
                  variant="outlined"
                  required
                  error={Boolean(errors?.last_name)}
                  helperText={errors?.last_name}
                />
              </Grid>

              <Grid className={classes.fieldGrid} size={{ xs: 12 }}>
                <Typography variant="subtitle1">
                  {t("Yrityksen nimi")}
                </Typography>
                <TextField
                  fullWidth
                  id="register-company_name"
                  name="company_name"
                  value={values.company_name || ""}
                  onChange={handleInputChange}
                  margin="dense"
                  variant="outlined"
                  required={!invitationToken}
                  disabled={Boolean(invitationToken)}
                  error={Boolean(errors?.company_name)}
                  helperText={
                    invitationToken
                      ? t("Yrityksen tiedot on jo rekisteröity")
                      : errors?.company_name
                  }
                />
              </Grid>

              <Grid className={classes.fieldGrid} size={{ xs: 12 }}>
                <Typography variant="subtitle1">
                  {t("Yrityksen y-tunnus")}
                </Typography>
                <TextField
                  fullWidth
                  id="register-business_id"
                  name="business_id"
                  value={values.business_id || ""}
                  onChange={handleInputChange}
                  margin="dense"
                  variant="outlined"
                  required={!invitationToken}
                  disabled={Boolean(invitationToken)}
                  error={Boolean(errors?.business_id)}
                  helperText={
                    invitationToken
                      ? t("Yrityksen tiedot on jo rekisteröity")
                      : errors?.business_id
                  }
                />
              </Grid>

              <Grid className={classes.fieldGrid} size={{ xs: 12 }}>
                <Typography variant="subtitle1">
                  {t("Sähköposti / käyttäjätunnus")}
                </Typography>
                <TextField
                  fullWidth
                  id="register-email"
                  name="email"
                  value={values.email}
                  onChange={handleInputChange}
                  type="email"
                  margin="dense"
                  variant="outlined"
                  required
                  error={Boolean(errors?.email)}
                  helperText={errors?.email}
                  slotProps={{
                    htmlInput: {
                      readOnly: invitationToken ? true : false,
                    },
                  }}
                />
              </Grid>

              <Grid className={classes.fieldGrid} size={{ xs: 12 }}>
                <Typography variant="subtitle1">{t("Salasana")}</Typography>
                <TextField
                  fullWidth
                  id="register-password1"
                  name="password1"
                  type="password"
                  value={values.password1}
                  onChange={handleInputChange}
                  margin="dense"
                  variant="outlined"
                  required
                  error={Boolean(errors?.password1)}
                  helperText={errors?.password1}
                />
              </Grid>

              <Grid className={classes.fieldGrid} size={{ xs: 12 }}>
                <Typography variant="subtitle1">
                  {t("Salasana uudelleen")}
                </Typography>

                <TextField
                  fullWidth
                  id="register-password2"
                  name="password2"
                  type="password"
                  value={values.password2}
                  onChange={handleInputChange}
                  margin="dense"
                  variant="outlined"
                  required
                  error={Boolean(errors?.password2)}
                  helperText={errors?.password2}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ mb: "16px" }}>
                  <Grid
                    container
                    direction="row"
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Grid size={12}>
                      <Typography variant="subtitle1">
                        {t("Olen yrityksen nimenkirjoitusoikeudellinen")}
                      </Typography>
                    </Grid>

                    <Grid size={12}>
                      <ToggleButtonGroup
                        value={sign}
                        exclusive
                        onChange={handleSign}
                        aria-label="text alignment"
                      >
                        <ToggleButton
                          classes={{ selected: classes.toggleButtonSelected }}
                          className={classes.toggleButton}
                          value={true}
                          aria-label="left aligned"
                        >
                          {t("Kyllä")}
                        </ToggleButton>
                        <ToggleButton
                          classes={{ selected: classes.toggleButtonSelected }}
                          className={classes.toggleButton}
                          value={false}
                          aria-label="right aligned"
                        >
                          {t("En")}
                        </ToggleButton>
                      </ToggleButtonGroup>
                      {errors?.sign ? (
                        <Typography
                          variant="caption"
                          color="error"
                          display="block"
                          sx={{ mt: 1 }}
                        >
                          {errors.sign}
                        </Typography>
                      ) : sign !== true ? (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                          sx={{ mt: 1 }}
                        >
                          {t("Vahvista allekirjoitusoikeus jatkaaksesi")}
                        </Typography>
                      ) : null}
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ mb: "16px" }}>
                  <Grid
                    container
                    direction="row"
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Grid size={12}>
                      <Typography variant="subtitle1">
                        Hyväksyn{" "}
                        <a href="https://opentaxi.fi/legal" target="_blank">
                          palvelusopimuksen
                        </a>
                        ,{" "}
                        <a href="https://opentaxi.fi/legal" target="_blank">
                          palvelukuvauksen
                        </a>{" "}
                        ja{" "}
                        <a href="https://opentaxi.fi/legal" target="_blank">
                          tietosuojaselosteen
                        </a>
                        <br />
                        Hyväksyn{" "}
                        <Link to={"/legal/prices"} target="_blank">
                          hinnaston
                        </Link>
                      </Typography>
                      {/*
                          {t('Hyväksyn')}&nbsp;
                          <a href="https://opentaxi.fi/legal" target="_blank">
                            {t('palvelusopimuksen')}
                          </a>
                          ,&nbsp;
                          <a href="https://opentaxi.fi/legal" target="_blank">
                            {t('palvelukuvauksen')}
                          </a>
                          &nbsp;{t('ja')}&nbsp;
                          <a href="https://opentaxi.fi/legal" target="_blank">
                            {t('tietosuojaselosteen')}
                          </a>
                          
                          {t('Hyväksyn')}&nbsp;
                          <Link
                            to={'/legal/prices'}
                            target="_blank"
                          >
                            {t('hinnaston')}
                          </Link>
                            */}
                    </Grid>

                    <Grid size={12}>
                      <ToggleButtonGroup
                        value={terms}
                        exclusive
                        onChange={handleTerms}
                        aria-label="text alignment"
                      >
                        <ToggleButton
                          classes={{ selected: classes.toggleButtonSelected }}
                          className={classes.toggleButton}
                          value={true}
                          aria-label="left aligned"
                        >
                          {t("Hyväksyn")}
                        </ToggleButton>
                        <ToggleButton
                          classes={{ selected: classes.toggleButtonSelected }}
                          className={classes.toggleButton}
                          value={false}
                          aria-label="right aligned"
                        >
                          {t("En")}
                        </ToggleButton>
                      </ToggleButtonGroup>
                      {errors?.terms ? (
                        <Typography
                          variant="caption"
                          color="error"
                          display="block"
                          sx={{ mt: 1 }}
                        >
                          {errors.terms}
                        </Typography>
                      ) : terms !== true ? (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                          sx={{ mt: 1 }}
                        >
                          {t("Hyväksy ehdot jatkaaksesi")}
                        </Typography>
                      ) : null}
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Grid
                  container
                  direction="row"
                  sx={{ justifyContent: "space-between", alignItems: "flex-end" }}
                >
                  <Grid size={12}>
                    <Typography variant="subtitle2">
                      {t("Haluatko palvelun käyttöön?")}
                      <a
                        href="https://www.opentaxi.fi/laheta-yrityksesi-tiedot/"
                        target="_blank"
                      >
                        <Typography variant="subtitle2">
                          {t("Ota yhteyttä")}
                        </Typography>
                      </a>
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    {errorSummary ? (
                      <Box ref={errorAlertRef} sx={{ mt: 2, mb: 1 }}>
                        <Alert severity="error">{errorSummary}</Alert>
                      </Box>
                    ) : null}
                    <ButtonPrimary
                      loading={saving}
                      type="submit"
                      disabled={saving}
                    >
                      {saving ? t("Rekisteröidään...") : t("Rekisteröidy")}
                    </ButtonPrimary>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </ViewContainer>
      ) : null}

      {registerState == 1 ? (
        <ViewContainer title={t("Olet rekisteröitynyt onnistuneesti!")}>
          <Stack
            direction="column"
            spacing={2}
            sx={{ alignItems: "center", width: 1 }}
          >
            <Typography variant="subtitle1">
              {t(
                "Tarkista ensimmäiseksi yrityksesi tiedot, jotta voit jatkaa palvelun käyttöä.",
              )}
            </Typography>
            <ButtonPrimary onClick={() => goDashboard()}>
              {t("Asia selvä")}
            </ButtonPrimary>
          </Stack>
        </ViewContainer>
      ) : null}

      {registerState == 2 ? (
        <ViewContainer title={t("Rekisteröinti epäonnistui!")} type="error">
          <Stack
            direction="column"
            spacing={2}
            sx={{ alignItems: "center", width: 1 }}
          >
            <Typography variant="subtitle1">
              {t(
                "Palvelu on käytössä vain kutsutuille yrityksille. Ota yhteyttä, jos haluat palvelun käyttöön!",
              )}
            </Typography>

            <ButtonPrimary onClick={() => goBack()}>
              {t("Takaisin")}
            </ButtonPrimary>
          </Stack>
        </ViewContainer>
      ) : null}
    </Fragment>
  );
};

export default RegisterAdmin;
